import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Twitter, Youtube, Mail } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import MusicPlayer from "@/components/MusicPlayer";
import Home from "@/pages/Home";
import Forum from "@/pages/Forum";
import TopicDetail from "@/pages/TopicDetail";
import HelpBoard from "@/pages/HelpBoard";
import Members from "@/pages/Members";
import Profile from "@/pages/Profile";
import Resources from "@/pages/Resources";
import Wallet from "@/pages/Wallet";
import Gallery from "@/pages/Gallery";
import ShoutOuts from "@/pages/ShoutOuts";
import Donate from "@/pages/Donate";
import SupportWall from "@/pages/SupportWall";
import Transparency from "@/pages/Transparency";
import Games from "@/pages/Games";
import Auth from "@/pages/Auth";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4A5568] font-heading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function Shell() {
  useEffect(() => {
    if (!sessionStorage.getItem("amir_visit_counted")) {
      sessionStorage.setItem("amir_visit_counted", "1");
      api.post("/visits").catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/forum" element={<Forum />} />
          <Route path="/forum/:id" element={<TopicDetail />} />
          <Route path="/help" element={<HelpBoard />} />
          <Route path="/members" element={<Members />} />
          <Route path="/members/:id" element={<Profile />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/nurses" element={<ShoutOuts />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/support" element={<SupportWall />} />
          <Route path="/token" element={<Transparency />} />
          <Route path="/games" element={<Games />} />
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <Wallet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="border-t border-[#6A0DAD]/30 py-10 text-center text-sm text-[#4A5568] font-heading">
        <p className="font-display text-2xl text-[#6A0DAD] mb-3">A Miracle is Real</p>
        <div className="flex items-center justify-center gap-4 mb-4">
          <a
            href="https://x.com/IanFluxFlynn"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-x"
            aria-label="Follow on X (@IanFluxFlynn)"
            title="X · @IanFluxFlynn"
            className="grid place-items-center w-10 h-10 rounded-full bg-white border border-[#6A0DAD]/40 text-[#6A0DAD] hover:bg-[#6A0DAD] hover:text-white transition-colors"
          >
            <Twitter size={18} />
          </a>
          <a
            href="https://youtube.com/@Ianflynn-3di"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-youtube"
            aria-label="Subscribe on YouTube (@Ianflynn-3di)"
            title="YouTube · @Ianflynn-3di"
            className="grid place-items-center w-10 h-10 rounded-full bg-white border border-[#6A0DAD]/40 text-[#6A0DAD] hover:bg-[#6A0DAD] hover:text-white transition-colors"
          >
            <Youtube size={18} />
          </a>
          <a
            href="mailto:flynngodisgood1@gmail.com"
            data-testid="social-email"
            aria-label="Email flynngodisgood1@gmail.com"
            title="Email · flynngodisgood1@gmail.com"
            className="grid place-items-center w-10 h-10 rounded-full bg-white border border-[#6A0DAD]/40 text-[#6A0DAD] hover:bg-[#6A0DAD] hover:text-white transition-colors"
          >
            <Mail size={18} />
          </a>
        </div>
        <p>Built with love for NICU families · AMIR Community</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <MusicPlayer />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
